import {
  Filter,
  Identifiable,
  List,
  Project,
  Tag,
  TimeSheet,
  TimeSheetFilter,
} from "@planzone/dto";
import { State } from "./types";

function areArrayEqual(first: string[], second: string[]): boolean {
  const set = new Set([...first, ...second]);

  return set.size === first.length && set.size === second.length;
}

function hasSameKeys(first: Filter, second: Filter): boolean {
  return areArrayEqual(Object.keys(first), Object.keys(second));
}

function extractId<I extends Object, K extends keyof I>(
  col: I[],
  keyName: K,
): I[K][] {
  return col.map((o) => o[keyName]);
}

function isTagList(key: string): boolean {
  return key === "projectsElementsTags" || key === "projectsTags";
}

function areFiltersEqual(first: Filter, second: Filter): boolean {
  if (!hasSameKeys(first, second)) {
    return false;
  }

  return Object.entries(first).reduce(
    (areEqual, [key, value]) =>
      !areEqual || !(key in second)
        ? false
        : isTagList(key)
          ? areArrayEqual(
              extractId(value as Tag[], "name"),
              extractId(second[key as keyof typeof second] as Tag[], "name"),
            )
          : areArrayEqual(
              extractId(value as Identifiable<any>[], "uuid"),
              extractId(
                second[key as keyof typeof second] as Identifiable<any>[],
                "uuid",
              ),
            ),
    true,
  );
}

function timeSheetFilterToFilter(filter: TimeSheetFilter): Filter {
  const {
    minDate,
    maxDate,
    includeDeactivated,
    includeRemovedFromProject,
    includeCompletedTasks,
    ...nextFilter
  } = filter;

  return nextFilter;
}

function areFiltersMatching(a: TimeSheetFilter, b: TimeSheetFilter) {
  return (
    a.minDate === b.minDate &&
    a.maxDate === b.maxDate &&
    a.includeDeactivated === b.includeDeactivated &&
    a.includeRemovedFromProject === b.includeRemovedFromProject &&
    areFiltersEqual(timeSheetFilterToFilter(a), timeSheetFilterToFilter(b))
  );
}

function matchRequest(
  req: State[number],
  project: Identifiable<Project>,
  filter: TimeSheetFilter,
  size?: number,
): boolean {
  return (
    req.project.uuid === project.uuid &&
    // (size ? req.size === size : true) &&
    areFiltersMatching(req.filter, filter)
  );
}

function mergeTS(ts: TimeSheet, partialTS: TimeSheet): TimeSheet {
  function replaceSpentTimes(
    original: TimeSheet["spentTimes"],
    partial: TimeSheet["spentTimes"],
  ): TimeSheet["spentTimes"] {
    const partialSet = new Set(partial.map((st) => st.date));
    return original
      .filter((st) => !partialSet.has(st.date)) // Keep only original entries not in the partial
      .concat(partial); // Add partial data
  }

  function replaceLists(
    originalLists: TimeSheet["resources"][number]["lists"],
    partialLists: TimeSheet["resources"][number]["lists"],
  ): TimeSheet["resources"][number]["lists"] {
    const result: TimeSheet["resources"][number]["lists"] = [...originalLists];

    partialLists.forEach((partialList) => {
      const existingListIndex = result.findIndex(
        (list) =>
          list.uuid === partialList.uuid ||
          ("activityRoot" in list &&
            (list.activityRoot as Identifiable<List>).uuid ===
              partialList.uuid) ||
          ("activityRoot" in partialList &&
            (partialList.activityRoot as Identifiable<List>).uuid ===
              list.uuid),
      );

      if (existingListIndex !== -1) {
        const existingList = result[existingListIndex];

        // Replace spentTimes and subLists
        result[existingListIndex] = {
          ...existingList,
          ...partialList,
          spentTimes: replaceSpentTimes(
            existingList.spentTimes,
            partialList.spentTimes,
          ),
          subLists: replaceLists(existingList.subLists, partialList.subLists),
          tasks: replaceTasks(existingList.tasks, partialList.tasks),
        };
      } else {
        result.push(partialList);
      }
    });

    return result;
  }

  function replaceTasks(
    originalTasks: TimeSheet["resources"][number]["lists"][number]["tasks"],
    partialTasks: TimeSheet["resources"][number]["lists"][number]["tasks"],
  ): TimeSheet["resources"][number]["lists"][number]["tasks"] {
    const result: TimeSheet["resources"][number]["lists"][number]["tasks"] = [
      ...originalTasks,
    ];

    partialTasks.forEach((partialTask) => {
      const existingTaskIndex = result.findIndex(
        (task) => task.uuid === partialTask.uuid,
      );

      if (existingTaskIndex !== -1) {
        const existingTask = result[existingTaskIndex];

        // Replace all task data with partial task data, ensuring we keep any non-overwritten fields
        result[existingTaskIndex] = {
          ...existingTask,
          ...partialTask,
          spentTimes: replaceSpentTimes(
            existingTask.spentTimes,
            partialTask.spentTimes,
          ),
          timeRecords: partialTask.timeRecords, // Replace timeRecords
        };
      } else {
        result.push(partialTask);
      }
    });

    return result;
  }

  function replaceResources(
    originalResources: TimeSheet["resources"],
    partialResources: TimeSheet["resources"],
  ): TimeSheet["resources"] {
    const result: TimeSheet["resources"] = [...originalResources];

    partialResources.forEach((partialResource) => {
      const existingResourceIndex = result.findIndex(
        (resource) => resource.uuid === partialResource.uuid,
      );

      if (existingResourceIndex !== -1) {
        const existingResource = result[existingResourceIndex];

        // Replace all resource data
        result[existingResourceIndex] = {
          ...existingResource,
          ...partialResource,
          spentTimes: replaceSpentTimes(
            existingResource.spentTimes,
            partialResource.spentTimes,
          ),
          lists: replaceLists(existingResource.lists, partialResource.lists),
        };
      } else {
        result.push(partialResource);
      }
    });

    return result;
  }

  return {
    spentTimes: replaceSpentTimes(ts.spentTimes, partialTS.spentTimes),
    resources: replaceResources(ts.resources, partialTS.resources),
  };
}

export { matchRequest, mergeTS };
